import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  VStack,
  HStack,
  Select,
  Progress,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
  Card,
  CardBody,
  InputGroup,
  InputLeftElement,
  FormHelperText,
  Badge,
  Divider,
} from "@chakra-ui/react";
import {
  FiHome,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUsers,
  FiArrowLeft,
  FiArrowRight,
  FiCheck
} from "react-icons/fi";
import Icon from "components/icons/Icon";
import { useAuth } from "contexts/AuthContext";

const steps = [
  { title: 'Agency Info', description: 'Basic agency details' },
  { title: 'Owner Info', description: 'Personal information' },
  { title: 'Review', description: 'Confirm details' }
];

function AgencySignup() {
  const navigate = useNavigate();
  const toast = useToast();
  const { signup, isLoading, error: authError, clearError } = useAuth();
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  // Form state - aligned with Agency model
  const [formData, setFormData] = useState({
    // Agency fields (from Agency model)
    name: '',                    // Agency name (required)
    description: '',             // Agency description  
    address: '',                 // Agency address
    contact_phone: '',           // Agency contact phone
    contact_email: '',           // Agency contact email (will use same as owner email)

    // Owner/User fields (from User model) 
    owner_name: '',              // Owner's full name (for user account)
    email: '',                   // Owner's email (for login + agency contact)
    password: '',                // Owner's password
    confirmPassword: ''          // Password confirmation
  });
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.400");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const bgColor = useColorModeValue('gray.50', 'navy.900');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const borderColorLight = useColorModeValue('gray.100', 'gray.700');
  const iconColor = useColorModeValue('brand.500', 'brand.400');
  const successIconColor = useColorModeValue('green.500', 'green.400');
  const cardShadow = useColorModeValue('xl', 'dark-lg');
  const buttonBg = useColorModeValue('brand.500', 'brand.400');
  const buttonHover = useColorModeValue('brand.600', 'brand.500');
  const buttonActive = useColorModeValue('brand.700', 'brand.600');
  const inputColor = useColorModeValue('gray.700', 'white');
  const inputBg = useColorModeValue('white', 'navy.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');
  const placeholderColor = useColorModeValue('gray.400', 'gray.400');

  // Validation functions
  const validateStep = (step: number): boolean => {
    const errors: { [key: string]: string } = {};

    if (step === 0) {
      // Agency information validation
      if (!formData.name.trim()) errors.name = 'Agency name is required';
      if (!formData.description.trim()) errors.description = 'Agency description is required';
      if (!formData.address.trim()) errors.address = 'Agency address is required';
      if (formData.contact_phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.contact_phone)) {
        errors.contact_phone = 'Invalid phone format';
      }
    } else if (step === 1) {
      // Owner information validation
      if (!formData.owner_name.trim()) errors.owner_name = 'Full name is required';
      if (!formData.email.trim()) errors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email format';
      if (!formData.password) errors.password = 'Password is required';
      else if (formData.password.length < 8) errors.password = 'Password must be at least 8 characters';
      if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general errors
    if (error) setError('');
    if (authError) clearError();
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(activeStep + 1);
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    setActiveStep(activeStep - 1);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Final validation
    if (!validateStep(0) || !validateStep(1)) {
      toast({
        title: "Validation Error",
        description: "Please fix all validation errors before submitting",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setError('');

    try {
      const result = await signup({
        name: formData.owner_name,
        email: formData.email,
        password: formData.password,
        role: 'agency',
        // Pass agency-specific data
        description: formData.description,
        address: formData.address,
        contactPhone: formData.contact_phone
      });

      if (result.success) {
        toast({
          title: "📋 Application Submitted!",
          description: `Thank you! Your agency "${formData.name}" application has been submitted for admin approval. You'll receive an email once approved.`,
          status: "success",
          duration: 8000,
          isClosable: true,
        });

        // Redirect to sign in page with a message
        setTimeout(() => {
          navigate('/auth/sign-in', {
            state: {
              message: 'Your agency application is under review. You will be notified once approved.'
            }
          });
        }, 2000);
      } else {
        const errorMsg = result.error || 'Registration failed. Please try again.';
        setError(errorMsg);

        toast({
          title: "Registration Failed",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      let errorMsg = 'An unexpected error occurred. Please try again.';

      // Better error handling
      if (err?.message) {
        errorMsg = err.message;
      } else if (typeof err === 'string') {
        errorMsg = err;
      }

      setError(errorMsg);

      toast({
        title: "Registration Error",
        description: errorMsg,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <VStack spacing={{ base: 5, md: 6 }} align="stretch">
            <Box textAlign="center" mb={{ base: 4, md: 4 }}>
              <Icon as={FiHome} boxSize={{ base: 8, sm: 10, md: 12 }} color={iconColor} mb={{ base: 2, md: 3 }} />
              <Heading size={{ base: "sm", md: "md" }} color={textColor} mb={2}>
                Tell us about your agency
              </Heading>
              <Text color={textColorSecondary} fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 0 }} lineHeight="base">
                We'll use this information to set up your fitness business
              </Text>
            </Box>

            <FormControl isRequired isInvalid={!!fieldErrors.name}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Agency Name<Text as="span" color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiHome} color="gray.400" />
                </InputLeftElement>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder='e.g., FitPro Gym, Wellness Center'
                  size={{ base: 'md', md: 'lg' }}
                  h={{ base: '44px', md: '48px' }}
                  color={inputColor}
                  bg={inputBg}
                  borderColor={inputBorder}
                  _placeholder={{ color: placeholderColor }}
                />
              </InputGroup>
              {fieldErrors.name && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.name}</Text>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!fieldErrors.description}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Agency Description<Text as="span" color={brandStars}>*</Text>
              </FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder='Describe your fitness services, specializations, and what makes your agency unique...'
                rows={4}
                resize="vertical"
                color={inputColor}
                bg={inputBg}
                borderColor={inputBorder}
                _placeholder={{ color: placeholderColor }}
              />
              <FormHelperText>This will help trainers and clients understand your services</FormHelperText>
              {fieldErrors.description && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.description}</Text>
              )}
            </FormControl>

            <FormControl isInvalid={!!fieldErrors.contact_phone}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Contact Phone (Optional)
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiPhone} color="gray.400" />
                </InputLeftElement>
                <Input
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  placeholder='+1 (555) 123-4567'
                  size={{ base: 'md', md: 'lg' }}
                  h={{ base: '44px', md: '48px' }}
                  color={inputColor}
                  bg={inputBg}
                  borderColor={inputBorder}
                  _placeholder={{ color: placeholderColor }}
                />
              </InputGroup>
              <FormHelperText>Primary contact number for the agency</FormHelperText>
              {fieldErrors.contact_phone && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.contact_phone}</Text>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!fieldErrors.address}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Business Address<Text as="span" color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiMapPin} color="gray.400" />
                </InputLeftElement>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder='123 Main St, City, State, ZIP'
                  size={{ base: 'md', md: 'lg' }}
                  h={{ base: '44px', md: '48px' }}
                  color={inputColor}
                  bg={inputBg}
                  borderColor={inputBorder}
                  _placeholder={{ color: placeholderColor }}
                />
              </InputGroup>
              {fieldErrors.address && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.address}</Text>
              )}
            </FormControl>


          </VStack>
        );

      case 1:
        return (
          <VStack spacing={{ base: 5, md: 6 }} align="stretch">
            <Box textAlign="center" mb={{ base: 4, md: 4 }}>
              <Icon as={FiUser} boxSize={{ base: 8, sm: 10, md: 12 }} color={iconColor} mb={{ base: 2, md: 3 }} />
              <Heading size={{ base: "sm", md: "md" }} color={textColor} mb={2}>
                Owner Information
              </Heading>
              <Text color={textColorSecondary} fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 0 }} lineHeight="base">
                Tell us about yourself as the agency owner
              </Text>
            </Box>

            <FormControl isRequired isInvalid={!!fieldErrors.owner_name}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Your Full Name<Text as="span" color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiUser} color="gray.400" />
                </InputLeftElement>
                <Input
                  name="owner_name"
                  value={formData.owner_name}
                  onChange={handleInputChange}
                  placeholder='Enter your full name'
                  size={{ base: 'md', md: 'lg' }}
                  h={{ base: '44px', md: '48px' }}
                  color={inputColor}
                  bg={inputBg}
                  borderColor={inputBorder}
                  _placeholder={{ color: placeholderColor }}
                />
              </InputGroup>
              <FormHelperText>This will be used for your account login</FormHelperText>
              {fieldErrors.owner_name && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.owner_name}</Text>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!fieldErrors.email}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Email Address<Text as="span" color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiMail} color="gray.400" />
                </InputLeftElement>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder='your@email.com'
                  size={{ base: 'md', md: 'lg' }}
                  h={{ base: '44px', md: '48px' }}
                  color={inputColor}
                  bg={inputBg}
                  borderColor={inputBorder}
                  _placeholder={{ color: placeholderColor }}
                />
              </InputGroup>
              <FormHelperText>This will be used for login and as the agency contact email</FormHelperText>
              {fieldErrors.email && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.email}</Text>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!fieldErrors.password}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Password<Text as="span" color={brandStars}>*</Text>
              </FormLabel>
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder='Create a strong password'
                size={{ base: 'md', md: 'lg' }}
                h={{ base: '44px', md: '48px' }}
                color={inputColor}
                bg={inputBg}
                borderColor={inputBorder}
                _placeholder={{ color: placeholderColor }}
              />
              <FormHelperText>Must be at least 8 characters long</FormHelperText>
              {fieldErrors.password && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.password}</Text>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!fieldErrors.confirmPassword}>
              <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                Confirm Password<Text as="span" color={brandStars}>*</Text>
              </FormLabel>
              <Input
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder='Confirm your password'
                size={{ base: 'md', md: 'lg' }}
                h={{ base: '44px', md: '48px' }}
                color={inputColor}
                bg={inputBg}
                borderColor={inputBorder}
                _placeholder={{ color: placeholderColor }}
              />
              {fieldErrors.confirmPassword && (
                <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.confirmPassword}</Text>
              )}
            </FormControl>
          </VStack>
        );

      case 2:
        return (
          <VStack spacing={{ base: 5, md: 6 }} align="stretch">
            <Box textAlign="center" mb={{ base: 4, md: 4 }}>
              <Icon as={FiCheck} boxSize={{ base: 8, sm: 10, md: 12 }} color={successIconColor} mb={{ base: 2, md: 3 }} />
              <Heading size={{ base: "sm", md: "md" }} color={textColor} mb={2}>
                Review Your Information
              </Heading>
              <Text color={textColorSecondary} fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 0 }} lineHeight="base">
                Please review your details before creating your account
              </Text>
            </Box>

            <Card bg={cardBg} borderColor={inputBorder}>
              <CardBody p={{ base: 4, md: 6 }}>
                <VStack spacing={{ base: 6, md: 4 }} align="stretch">
                  <Box>
                    <Text fontWeight="600" color={textColor} mb={3} fontSize={{ base: "md", md: "lg" }}>Agency Details</Text>
                    <VStack spacing={3} align="stretch" pl={{ base: 2, md: 4 }}>
                      <Flex direction={{ base: "column", sm: "row" }} justify={{ base: "flex-start", sm: "space-between" }} align={{ base: "flex-start", sm: "center" }}>
                        <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary} mb={{ base: 1, sm: 0 }} minW={{ base: "auto", sm: "80px" }}>Name:</Text>
                        <Text fontSize={{ base: "sm", md: "sm" }} fontWeight="500" color={textColor} textAlign={{ base: "left", sm: "right" }}>{formData.name}</Text>
                      </Flex>
                      <Flex direction={{ base: "column", sm: "row" }} justify={{ base: "flex-start", sm: "space-between" }} align={{ base: "flex-start", sm: "flex-start" }}>
                        <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary} mb={{ base: 1, sm: 0 }} minW={{ base: "auto", sm: "80px" }}>Description:</Text>
                        <Text fontSize={{ base: "sm", md: "sm" }} textAlign={{ base: "left", sm: "right" }} maxW={{ base: "100%", sm: "250px" }} color={textColor}>{formData.description}</Text>
                      </Flex>
                      <Flex direction={{ base: "column", sm: "row" }} justify={{ base: "flex-start", sm: "space-between" }} align={{ base: "flex-start", sm: "flex-start" }}>
                        <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary} mb={{ base: 1, sm: 0 }} minW={{ base: "auto", sm: "80px" }}>Address:</Text>
                        <Text fontSize={{ base: "sm", md: "sm" }} textAlign={{ base: "left", sm: "right" }} maxW={{ base: "100%", sm: "200px" }} color={textColor}>{formData.address}</Text>
                      </Flex>
                      {formData.contact_phone && (
                        <Flex direction={{ base: "column", sm: "row" }} justify={{ base: "flex-start", sm: "space-between" }} align={{ base: "flex-start", sm: "center" }}>
                          <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary} mb={{ base: 1, sm: 0 }} minW={{ base: "auto", sm: "80px" }}>Phone:</Text>
                          <Text fontSize={{ base: "sm", md: "sm" }} color={textColor} textAlign={{ base: "left", sm: "right" }}>{formData.contact_phone}</Text>
                        </Flex>
                      )}
                    </VStack>
                  </Box>

                  <Divider borderColor={borderColor} />

                  <Box>
                    <Text fontWeight="600" color={textColor} mb={3} fontSize={{ base: "md", md: "lg" }}>Owner Information</Text>
                    <VStack spacing={3} align="stretch" pl={{ base: 2, md: 4 }}>
                      <Flex direction={{ base: "column", sm: "row" }} justify={{ base: "flex-start", sm: "space-between" }} align={{ base: "flex-start", sm: "center" }}>
                        <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary} mb={{ base: 1, sm: 0 }} minW={{ base: "auto", sm: "80px" }}>Name:</Text>
                        <Text fontSize={{ base: "sm", md: "sm" }} fontWeight="500" color={textColor} textAlign={{ base: "left", sm: "right" }}>{formData.owner_name}</Text>
                      </Flex>
                      <Flex direction={{ base: "column", sm: "row" }} justify={{ base: "flex-start", sm: "space-between" }} align={{ base: "flex-start", sm: "center" }}>
                        <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary} mb={{ base: 1, sm: 0 }} minW={{ base: "auto", sm: "80px" }}>Email:</Text>
                        <Text fontSize={{ base: "sm", md: "sm" }} color={textColor} textAlign={{ base: "left", sm: "right" }} wordBreak="break-all">{formData.email}</Text>
                      </Flex>
                    </VStack>
                  </Box>
                </VStack>
              </CardBody>
            </Card>

            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="500">Approval Required</Text>
                <Text fontSize="xs">
                  Your agency application will be reviewed by our admin team. You'll receive an email notification once your account is approved and activated.
                </Text>
              </VStack>
            </Alert>
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      minH="100vh"
      bg={bgColor}
      pt={{ base: "16px", sm: "20px", md: "40px" }}
      pb={{ base: "20px", sm: "30px", md: "60px" }}
      px={{ base: '16px', sm: '20px', md: '40px' }}>

      <VStack spacing={{ base: 4, sm: 6, md: 8 }} maxW="800px" mx="auto">
        {/* Header */}
        <Box textAlign='center' px={{ base: 2, sm: 4, md: 0 }}>
          <Heading color={textColor} fontSize={{ base: 'lg', sm: 'xl', md: '2xl', lg: '3xl' }} mb={{ base: 2, md: 2 }} lineHeight="shorter">
            Create Your Agency Account
          </Heading>
          <Text color={textColorSecondary} fontSize={{ base: 'xs', sm: 'sm', md: 'md' }} maxW={{ base: '280px', sm: '400px', md: 'none' }} mx="auto" lineHeight="base">
            Join FitnessService and start managing your trainers and clients
          </Text>
        </Box>

        {/* Progress Stepper */}
        <Box w="100%" maxW={{ base: "100%", sm: "500px", md: "600px" }} display="flex" justifyContent="center">
          {/* Mobile Progress Indicator */}
          <Box display={{ base: "block", md: "none" }} mb={4} w="100%" maxW="300px">
            <VStack spacing={3} align="center" w="100%">
              <Text fontSize="xs" color={textColorSecondary} textAlign="center">
                Step {activeStep + 1} of {steps.length}
              </Text>
              <Box w="100%" display="flex" justifyContent="center">
                <Progress 
                  value={(activeStep + 1) / steps.length * 100} 
                  colorScheme="brand" 
                  size="sm" 
                  borderRadius="full" 
                  w="160px"
                />
              </Box>
              <Text fontSize="sm" fontWeight="500" color={textColor} textAlign="center">
                {steps[activeStep].title}
              </Text>
              <Text fontSize="xs" color={textColorSecondary} textAlign="center" maxW="240px" lineHeight="base">
                {steps[activeStep].description}
              </Text>
            </VStack>
          </Box>
          
          {/* Desktop Stepper */}
          <Box display={{ base: "none", md: "block" }}>
            <Stepper index={activeStep} colorScheme="brand" size="lg">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <Box flexShrink="0">
                    <StepTitle fontSize="md">{step.title}</StepTitle>
                    <StepDescription fontSize="sm">
                      {step.description}
                    </StepDescription>
                  </Box>
                  <StepSeparator />
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>

        {/* Main Form Card */}
        <Card w="100%" maxW={{ base: "100%", sm: "500px", md: "700px" }} borderRadius={{ base: "8px", sm: "12px", md: "20px" }} boxShadow={cardShadow} bg={cardBg}>
          <CardBody p={{ base: '16px', sm: '24px', md: '40px' }}>
            {(error || authError) && (
              <Alert status="error" mb="20px" borderRadius="8px">
                <AlertIcon />
                {error || authError}
              </Alert>
            )}

            {/* Form content - no form wrapper here */}
            {renderStepContent()}

            {/* Navigation Buttons */}
            <Flex direction={{ base: "column", sm: "row" }} justify="space-between" mt={8} pt={6} borderTop="1px solid" borderColor={borderColor} gap={{ base: 3, sm: 0 }}>
              <Button
                leftIcon={<Icon as={FiArrowLeft} />}
                variant="ghost"
                onClick={handlePrevious}
                isDisabled={activeStep === 0}
                color={textColorSecondary}
                size={{ base: "md", md: "lg" }}
                order={{ base: 2, sm: 1 }}
                w={{ base: "full", sm: "auto" }}
              >
                Previous
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button
                  rightIcon={<Icon as={FiArrowRight} />}
                  bg={buttonBg}
                  color="white"
                  _hover={{ bg: buttonHover }}
                  _active={{ bg: buttonActive }}
                  onClick={handleNext}
                  size={{ base: "md", md: "lg" }}
                  order={{ base: 1, sm: 2 }}
                  w={{ base: "full", sm: "auto" }}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  bg={buttonBg}
                  color="white"
                  _hover={{ bg: buttonHover }}
                  _active={{ bg: buttonActive }}
                  size={{ base: "md", md: "lg" }}
                  rightIcon={<Icon as={FiCheck} />}
                  isLoading={isLoading}
                  loadingText="Creating Agency..."
                  disabled={isLoading}
                  onClick={handleSubmit}
                  order={{ base: 1, sm: 2 }}
                  w={{ base: "full", sm: "auto" }}
                  _disabled={{ 
                    bg: useColorModeValue('gray.300', 'gray.600'), 
                    cursor: 'not-allowed' 
                  }}
                >
                  Submit for Approval
                </Button>
              )}
            </Flex>

            {/* Sign In Link */}
            <Text fontSize='sm' color={textColorSecondary} textAlign='center' mt={6} pt={4} borderTop="1px solid" borderColor={borderColorLight}>
              Already have an account?{' '}
              <Text
                as="button"
                color={iconColor}
                fontWeight="500"
                onClick={() => navigate('/auth/sign-in')}
                _hover={{ textDecoration: 'underline' }}
              >
                Sign In
              </Text>
            </Text>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
}

export default AgencySignup;